import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { APIClient } from '../../utils/APIClient';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from "../../settings/local_settings";
import { AgentWebSocketsClient } from '../../websockets/WebSocketsClient';
import { CollaborativeMessageBubble } from './shared/CollaborativeMessageBubble';
import { AgentThinkingIndicator } from '../client/agent/AgentThinkingIndicator';
import { AgentInputArea } from '../client/agent/AgentInputArea';
import { AgentEmptyState } from '../client/agent/AgentEmptyState';
import { AgentContextSelector } from '../client/agent/AgentContextSelector';
import { AgentClientSelector } from './AgentClientSelector';
import { ChatHeader } from './shared/ChatHeader';
import { useAgentSessions } from './shared/AgentSessionContext';


const STEP_LABELS = {
    processing: 'agent_chat.step_processing',
    discovery: 'agent_chat.step_discovery',
    validation: 'agent_chat.step_validation',
    execution: 'agent_chat.step_execution',
    save: 'agent_chat.step_save',
};

export const ChatPage = ({ clients, isLoadingClients }) => {
    const { t } = useTranslation();
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const { activeSession, allSessions } = useAgentSessions();
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [currentStep, setCurrentStep] = useState(null);
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [clientData, setClientData] = useState(null);
    const [isLoadingClient, setIsLoadingClient] = useState(false);
    const [scope, setScope] = useState(null);
    const [debugMode, setDebugMode] = useState(false);
    const messagesEndRef = useRef(null);
    const scopeRef = useRef(null);
    const prevSessionIdRef = useRef(undefined);

    useEffect(() => {
        scopeRef.current = scope;
    }, [scope]);

    useEffect(() => {
        const cameFromNewChat = !prevSessionIdRef.current;
        prevSessionIdRef.current = sessionId;

        if (sessionId) {
            APIClient.getAgentSessionHistory(sessionId).then(data => {
                if (data && data.messages) {
                    setMessages(data.messages);
                }
            });

            if (cameFromNewChat) {
                if (!scopeRef.current) {
                    setScope({ adAccountId: null });
                }
            } else {
                setSelectedClientId(null);
                setClientData(null);
                scopeRef.current = null;
                setScope({ adAccountId: null });
            }
        } else {
            setMessages([]);
            setSelectedClientId(null);
            setClientData(null);
            scopeRef.current = null;
            setScope(null);
        }
    }, [sessionId]);

    useEffect(() => {
        if (!selectedClientId) return;
        setIsLoadingClient(true);
        setClientData(null);
        setScope(null);
        new APIClient(API_URL + 'client/' + selectedClientId).call('GET').then((data) => {
            if (data && data.status === 'OK') {
                const adAccounts = data.data.client_ad_accounts || [];
                setClientData({ adAccounts });
                if (adAccounts.length === 0) {
                    setScope({ adAccountId: null });
                }
            } else {
                setScope({ adAccountId: null });
            }
        }).catch(() => {
            setScope({ adAccountId: null });
        }).finally(() => {
            setIsLoadingClient(false);
        });
    }, [selectedClientId]);

    const apiClient = useMemo(() => new APIClient(`${API_URL}agent/chat/`), []);

    const scrollToBottom = useCallback(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, isSending, scrollToBottom]);

    const handleClientSelected = useCallback((clientId) => {
        setSelectedClientId(clientId);
    }, []);

    const handleScopeSelected = useCallback((selectedScope) => {
        setScope(selectedScope);
    }, []);

    const handlePromptClick = useCallback((promptText) => {
        setInputValue(promptText);
        setTimeout(() => {
            const syntheticSend = document.querySelector('.agent-input__send-btn');
            if (syntheticSend && !syntheticSend.disabled) {
                syntheticSend.click();
            }
        }, 50);
    }, []);

    const handleSend = useCallback(async () => {
        const trimmed = (inputValue || '').trim();
        if (!trimmed || isSending) return;
        setIsSending(true);
        setCurrentStep(null);

        const newUserMsg = { role: 'user', text: trimmed };
        setMessages(prev => [...prev, newUserMsg]);
        setInputValue('');

        const requestId = crypto.randomUUID();
        let channel = null;

        channel = AgentWebSocketsClient.subscribe(`agent-progress-${requestId}`);
        if (channel) {
            channel.bind('agent-step', (data) => {
                const label = STEP_LABELS[data.step];
                setCurrentStep(label ? t(label) : data.step);
            });
        }

        try {
            const payload = { message: trimmed, request_id: requestId, debug: debugMode };
            if (sessionId) payload.session_id = sessionId;
            if (selectedClientId) payload.agency_client_id = parseInt(selectedClientId);
            if (scope && scope.adAccountId) {
                payload.ad_account_id = scope.adAccountId;
            }

            const response = await apiClient.call('POST', payload);

            if (!response) {
                setMessages(prev => [...prev, { role: 'error', text: t('agent_chat.error_no_response') }]);
                setIsSending(false);
                return;
            }

            // `!response.data` cubre los errores TIPADOS del backend (NOT_FOUND, NO_AGENCY): sólo
            // se miraba 'ERROR', asi que un rechazo tipado caia al `response.data.reply` de abajo,
            // reventaba con TypeError y el catch mostraba "no se pudo enviar" en vez del motivo real.
            if (response.status === 'ERROR' || !response.data) {
                setMessages(prev => [...prev, { role: 'error', text: response.error_message || t('agent_chat.error_generic') }]);
                setIsSending(false);
                return;
            }

            const reply = response.data.reply || '';
            const newSessionId = response.data.session_id;
            const toolTrace = response.data.tool_trace;

            if (!sessionId && newSessionId) {
                navigate(`/agent/chat/${newSessionId}`);
            }

            if (reply) {
                const assistantMsg = { role: 'assistant', text: reply };
                if (toolTrace) assistantMsg.tool_trace = toolTrace;
                setMessages(prev => [...prev, assistantMsg]);
            }
        } catch (e) {
            setMessages(prev => [...prev, { role: 'error', text: t('agent_chat.error_send_failed') }]);
        } finally {
            if (channel) {
                channel.unbind_all();
                AgentWebSocketsClient.unsubscribe(`agent-progress-${requestId}`);
            }
            setCurrentStep(null);
            setIsSending(false);
        }
    }, [apiClient, inputValue, isSending, sessionId, selectedClientId, scope, debugMode, navigate, t]);

    // Nombre del cliente seleccionado para el badge
    const selectedClientName = useMemo(() => {
        if (selectedClientId && clients) {
            const client = clients.find(c => c.id === selectedClientId);
            if (client) return client.name;
        }
        if (sessionId && allSessions) {
            const session = allSessions.find(s => s.session_id === sessionId);
            if (session && session.client_name) return session.client_name;
        }
        return null;
    }, [selectedClientId, clients, sessionId, allSessions]);

    // Descripcion del scope seleccionado para el badge
    const scopeInfo = useMemo(() => {
        if (scope) {
            const acc = (scope.adAccountId && clientData)
                ? clientData.adAccounts.find(a => a.id === scope.adAccountId)
                : null;
            if (acc) {
                return { label: acc.name, type: 'accounts', channel: acc.channel || null, accountId: acc.account_id || null };
            }
        }
        if (sessionId && allSessions) {
            const session = allSessions.find(s => s.session_id === sessionId);
            if (session && session.scope_ad_account_name) {
                return {
                    label: session.scope_ad_account_name,
                    type: 'accounts',
                    channel: session.scope_ad_account_channel || null,
                    accountId: session.scope_ad_account_external_id || null,
                };
            }
        }
        return null;
    }, [scope, clientData, sessionId, allSessions]);

    const isSharedSession = activeSession && activeSession.visibility && activeSession.visibility !== 'PRIVATE';

    const hasContextData = clientData && clientData.adAccounts.length > 0;
    const showClientSelector = !sessionId && !selectedClientId;
    const showLoadingClient = !sessionId && selectedClientId && isLoadingClient;
    const showContextSelector = !sessionId && selectedClientId && clientData && scope === null && hasContextData;
    const showEmptyState = !sessionId && scope !== null && messages.length === 0 && !isSending;
    const showInput = sessionId || scope !== null;

    return (
        <div className="agent-chat">
            <ChatHeader
                clientName={selectedClientName}
                scopeLabel={scopeInfo?.label}
                scopeType={scopeInfo?.type}
                scopeChannel={scopeInfo?.channel}
                scopeAccountId={scopeInfo?.accountId}
                session={activeSession}
                debugToggle={
                    <button
                        className={`agent-chat__debug-btn ${debugMode ? '-active' : ''}`}
                        onClick={() => setDebugMode(prev => !prev)}
                        title="Debug mode"
                    >
                        <i className="fas fa-bug"></i>
                    </button>
                }
            />

            <div className="agent-chat__messages">
                {showClientSelector && (
                    <AgentClientSelector
                        clients={clients || []}
                        isLoading={isLoadingClients}
                        onClientSelected={handleClientSelected}
                    />
                )}

                {showLoadingClient && (
                    <div className="agent-context-selector">
                        <div className="agent-context-selector__icon">
                            <i className="fas fa-spinner fa-spin"></i>
                        </div>
                    </div>
                )}

                {showContextSelector && (
                    <AgentContextSelector
                        adAccounts={clientData.adAccounts}
                        onScopeSelected={handleScopeSelected}
                    />
                )}

                {showEmptyState && (
                    <AgentEmptyState onPromptClick={handlePromptClick} />
                )}

                {!showClientSelector && !showLoadingClient && !showContextSelector && !showEmptyState && messages.map((m, idx) => (
                    <CollaborativeMessageBubble key={idx} message={m} isSharedSession={isSharedSession} />
                ))}

                {isSending && (
                    <AgentThinkingIndicator stepLabel={currentStep} />
                )}
                <div ref={messagesEndRef} />
            </div>

            {showInput && (
                <AgentInputArea
                    value={inputValue}
                    onChange={setInputValue}
                    onSend={handleSend}
                    disabled={isSending}
                />
            )}
        </div>
    );
};
